import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationAssignee } from '@/Domain/api/Conversation/dto/ConversationAssign.dto';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/Domain/components/Shared/Select/Select';
import { StarsIcon } from '@/Domain/components/icons/Stars.icon';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { UserHelper } from '@/Domain/helper/User.helper';
import { useScenarioUsers } from '@/Domain/hooks/AI/useScenarioUsers.hook';
import { useUpdateConversation } from '@/Domain/hooks/Conversation/useUpdateConversation.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';

type Props = {
  conversationId: string;
  assignee: ConversationAssignee;
  location?: 'action-bar' | 'details-tab';
  onOpenChange?: (open: boolean) => void;
};

const getDefaultValue = (assignee: ConversationAssignee) => {
  switch (assignee.type) {
    case ConversationAssigneeEnum.HUMAN_OPERATOR:
      return assignee.user.id.toString();
    case ConversationAssigneeEnum.AI_ASSISTANT:
      return ConversationAssigneeEnum.AI_ASSISTANT;
    default:
      return ConversationAssigneeEnum.UNASSIGNED;
  }
};

export const AssignUser = ({ assignee, conversationId, location, onOpenChange }: Props) => {
  const { t } = useTranslation('inbox_v2');
  const { user } = useUser();
  const { users } = useScenarioUsers();
  const { handleUpdate } = useUpdateConversation(conversationId);

  const defaultValue = getDefaultValue(assignee);
  const [value, setValue] = useState<ConversationAssigneeEnum | string>(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleValueChange = (value: ConversationAssigneeEnum | string) => {
    setValue(value);

    if (value === ConversationAssigneeEnum.AI_ASSISTANT) {
      handleUpdate({ assignee: { type: ConversationAssigneeEnum.AI_ASSISTANT } });
      return;
    }

    if (value === ConversationAssigneeEnum.UNASSIGNED) {
      handleUpdate({ assignee: { type: ConversationAssigneeEnum.UNASSIGNED } });
      return;
    }

    if (user?.id.toString() === value) {
      handleUpdate({
        assignee: {
          type: ConversationAssigneeEnum.HUMAN_OPERATOR,
          user: { id: user.id, email: user.email },
        },
      });
      return;
    }

    handleUpdate({
      assignee: {
        type: ConversationAssigneeEnum.HUMAN_OPERATOR,
        user: { id: Number(value), email: null },
      },
    });
  };

  return (
    <div
      className={`${location === 'details-tab' ? 'grid grid-cols-2 items-center mb-6 gap-x-0' : ''}`}
    >
      {location === 'details-tab' && (
        <div className="flex items-center gap-2.5 text-sm font-semibold text-muted-foreground">
          <Icon icon="mingcute:user-2-fill" />
          <p>{t('assign_filter.label')}</p>
        </div>
      )}
      <Select value={value} onValueChange={handleValueChange} onOpenChange={onOpenChange}>
        <SelectTrigger
          variant={location === 'details-tab' ? 'unbordered' : 'bordered'}
          size={location === 'action-bar' ? 'xs' : 'sm'}
          iconName={location === 'action-bar' ? 'mingcute:up-line' : undefined}
          className={`${location === 'details-tab' && '!px-0'} ${location === 'action-bar' && '!px-2.5 min-w-0 overflow-hidden !max-w-[130px]'} max-w-[180px] bg-transparent ${value === ConversationAssigneeEnum.UNASSIGNED && 'text-red-500 font-semibold'}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="center">
          <SelectItem value={ConversationAssigneeEnum.AI_ASSISTANT}>
            <div className="flex items-center gap-2">
              <StarsIcon />
              {t('assign_filter.options.ai_agent')}
            </div>
          </SelectItem>
          <SelectSeparator />
          {(users || []).map((user) => (
            <SelectItem key={user.id} value={user.id.toString()}>
              {UserHelper.getFullName(user) || user.email}
            </SelectItem>
          ))}
          <SelectSeparator />
          <SelectItem value={ConversationAssigneeEnum.UNASSIGNED}>
            {t('assign_filter.options.unassigned')}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
